import { useState } from "react";
import { Link } from "react-router-dom";
import { openOwnerClubPortal } from "../../../utils/ownerClubPortal";
import ownerAPI from "../../../services/ownerApi";
import { TRIAL_DAYS } from "../../../constants/trial";
import OwnerClubSubscriptionCard from "../../common/ClubSubscription/OwnerClubSubscriptionCard";
import { IconPlus } from "../../ui/icons";
import { useToast } from "../../ui";
import { useOwnerDashboard } from "./OwnerDashboardContext";
import OwnerClubPayModal from "./OwnerClubPayModal";
import { OwnerCabinetHubLink } from "./OwnerCabinetHubLink";
import { OwnerPage, OwnerState } from "./OwnerPage";
import s from "./ownerCabinet.module.css";

export default function OwnerClubs() {
  const { data, loading, error, clubs, reload } = useOwnerDashboard();
  const toast = useToast();
  const [payClub, setPayClub] = useState(null);
  const [openingClubId, setOpeningClubId] = useState(null);
  const [deletingClubId, setDeletingClubId] = useState(null);

  const openClub = async (club) => {
    if (openingClubId) return;
    setOpeningClubId(club.id);
    try {
      await openOwnerClubPortal(club);
    } finally {
      setOpeningClubId(null);
    }
  };

  const deleteClub = async (club) => {
    if (deletingClubId) return;
    const name = String(club.name || "").trim();
    const typed = window.prompt(
      `Удалить клуб «${name}»?\n\nДоступ сотрудников и клиентов закроется. Восстановить нельзя.\n\nВведите название клуба для подтверждения:`,
      "",
    );
    if (typed == null) return;
    if (String(typed).trim() !== name) {
      toast.error("Название не совпало — клуб не удалён");
      return;
    }
    setDeletingClubId(club.id);
    try {
      await ownerAPI.deleteClub(club.id, name);
      toast.success("Клуб удалён");
      await reload();
    } catch (e) {
      toast.error(e.response?.data?.error || "Не удалось удалить клуб");
    } finally {
      setDeletingClubId(null);
    }
  };

  const expiringCount = clubs.filter(
    (c) =>
      c.billing?.is_expiring_soon ||
      c.billing?.is_demo_expired ||
      (!c.billing?.is_paid && !c.billing?.is_trial),
  ).length;

  const firstName = data?.user?.full_name?.split(" ")[0];

  if (loading) {
    return <OwnerState variant="loading">Загрузка…</OwnerState>;
  }

  if (error) {
    return <OwnerState variant="error">{error}</OwnerState>;
  }

  return (
    <OwnerPage
      title={firstName ? `Здравствуйте, ${firstName}` : "Мои клубы"}
      subtitle={
        clubs.length
          ? `${clubs.length} ${clubs.length === 1 ? "секция" : clubs.length < 5 ? "секции" : "секций"}`
          : "Создайте первую спортивную секцию"
      }
      actions={
        <Link to="/registraciya?new=1" className={s.addClubHeaderBtn}>
          <IconPlus width={18} height={18} aria-hidden />
          <span>Добавить</span>
        </Link>
      }
    >
      <div className={s.ownerHubPanel}>
        <OwnerCabinetHubLink />
      </div>

      {clubs.length > 0 ? (
        <div className={s.kpiRow}>
          <div className={s.kpiCard}>
            <div className={s.kpiLabel}>Клубов</div>
            <div className={s.kpiValue}>{clubs.length}</div>
          </div>
          <div
            className={`${s.kpiCard} ${expiringCount > 0 ? s.kpiCardWarn : ""}`}
          >
            <div className={s.kpiLabel}>К оплате</div>
            <div className={s.kpiValue}>{expiringCount}</div>
          </div>
        </div>
      ) : null}

      {clubs.length === 0 ? (
        <OwnerState variant="muted">
          <p className={s.emptyLead}>Пока нет клубов</p>
          <Link to="/registraciya" className={s.addClubHeaderBtn}>
            <IconPlus width={18} height={18} aria-hidden />
            <span>Создать первый клуб</span>
          </Link>
        </OwnerState>
      ) : (
        <div className={s.clubList}>
          {clubs.map((club) => (
            <OwnerClubSubscriptionCard
              key={club.id}
              club={club}
              variant="list"
              onOpenAdmin={openClub}
              onPay={setPayClub}
              onDelete={deletingClubId ? undefined : deleteClub}
            />
          ))}
          <Link to="/registraciya?new=1" className={s.addClubCard}>
            <span className={s.addClubIcon} aria-hidden>
              <IconPlus width={24} height={24} />
            </span>
            <span className={s.addClubLabel}>Новый клуб</span>
            <span className={s.addClubHint}>{TRIAL_DAYS} дней демо</span>
          </Link>
        </div>
      )}

      <OwnerClubPayModal
        club={payClub}
        open={Boolean(payClub)}
        onClose={() => setPayClub(null)}
      />
    </OwnerPage>
  );
}
